import { MillenniumDate } from "./MillenniumDate.js";
import { MillenniumWeek } from "./MillenniumWeek.js";
export class SimpleDateFormat {
    static months = [
        "januari",
        "februari",
        "mars",
        "april",
        "maj",
        "juni",
        "juli",
        "augusti",
        "september",
        "oktober",
        "november",
        "december",
    ];
    static months_short = [
        "jan",
        "feb",
        "mar",
        "apr",
        "maj",
        "jun",
        "jul",
        "aug",
        "sep",
        "okt",
        "nov",
        "dec",
    ];
    static days = ["söndag", "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag"];
    static firstDayOfWeek = null;
    static daysInFirstWeek = null;
    static formatToken(aDate, token, isWeekFormat = false) {
        if (isWeekFormat && ["j", "y", "N", "M", "b", "d"].indexOf(token[0]) !== -1) {
            aDate = new MillenniumWeek(aDate, SimpleDateFormat.firstDayOfWeek, SimpleDateFormat.daysInFirstWeek).getDateForWeekday(SimpleDateFormat.firstDayOfWeek);
        }
        switch (token) {
            // y Year
            case "jjjj":
            case "yyyy":
                return aDate.getYear();
            case "jj":
            case "yy":
                return `${aDate.getYear()}`.substring(2);
            // M Month in year
            case "NNNN":
            case "MMMM":
                return SimpleDateFormat.months[aDate.getMonth()];
            case "NNN":
            case "MMM":
                return SimpleDateFormat.months_short[aDate.getMonth()];
            case "NN":
            case "MM":
                return addZero(aDate.getMonth() + 1);
            case "N":
            case "M":
                return aDate.getMonth() + 1;
            // d Day in month
            case "bb":
            case "dd":
                return addZero(aDate.getDate());
            case "b":
            case "d":
                return aDate.getDate();
            // E Day in week
            case "LLLL":
            case "EEEE":
                return SimpleDateFormat.days[aDate.getDay()];
            case "LLL":
            case "EEE":
                return SimpleDateFormat.days[aDate.getDay()].substring(0, 3);
            case "LL":
            case "EE":
                return SimpleDateFormat.days[aDate.getDay()].substring(0, 2);
            case "L":
            case "E":
                return SimpleDateFormat.days[aDate.getDay()].substring(0, 1);
            // Y Year for week
            case "JJJJ":
            case "YYYY":
                return aDate.getWeekYear(SimpleDateFormat.firstDayOfWeek, SimpleDateFormat.daysInFirstWeek);
            case "JJ":
            case "YY":
            case "J":
            case "Y":
                return `${aDate.getWeekYear(SimpleDateFormat.firstDayOfWeek, SimpleDateFormat.daysInFirstWeek)}`.substring(2);
            // w Week in year
            case "vv":
            case "ww":
                return addZero(aDate.getWeek(false, SimpleDateFormat.firstDayOfWeek, SimpleDateFormat.daysInFirstWeek));
            case "v":
            case "w":
                return aDate.getWeek(false, SimpleDateFormat.firstDayOfWeek, SimpleDateFormat.daysInFirstWeek);
            // a Am/pm marker
            case "a":
                return aDate.getHours() >= 12 ? "PM" : "AM";
            // H Hour in day (0-23)
            case "HH":
                return addZero(aDate.getHours());
            case "H":
                return aDate.getHours();
            // k Hour in day (1-24)
            case "kk":
                return addZero(aDate.getHours() + 1);
            case "k":
                return aDate.getHours() + 1;
            // K Hour in am/pm (0-11)
            case "KK":
                return addZero(aDate.getHours() % 12);
            case "K":
                return aDate.getHours() % 12;
            // h Hour in am/pm (1-12)
            case "hh":
                let hours = aDate.getHours() % 12;
                return addZero(hours === 0 ? 12 : hours);
            case "h":
                let hrs = aDate.getHours() % 12;
                return hrs === 0 ? 12 : hrs;
            // e End hour in day (0-24)
            case "ee":
                return isMidnight(aDate) ? "24" : addZero(aDate.getHours());
            case "e":
                return isMidnight(aDate) ? "24" : aDate.getHours();
            // m Minute in hour
            case "mm":
                return addZero(aDate.getMinutes());
            case "m":
                return aDate.getMinutes();
            // s Second in minute
            case "ss":
                return addZero(aDate.getSeconds());
            case "s":
                return aDate.getSeconds();
        }
        throw new Error(`${token} is not a valid token.`);
    }
    static format = function (aDate, format) {
        if (containsEndDate(format)) {
            if (isMidnight(aDate)) {
                aDate = aDate.addDays?.(-1);
            }
        }
        const isWeekFormat = format.indexOf("w") !== -1;
        return getTokens(format).reduce((output, token) => {
            if (token.type === "literal") {
                return output + token.value;
            }
            return output + SimpleDateFormat.formatToken(aDate, token.value, isWeekFormat);
        }, "");
    };
    static setLocale(locale) {
        SimpleDateFormat.months = [
            "a_january",
            "b_february",
            "c_march",
            "d_april",
            "e_may",
            "f_june",
            "g_july",
            "h_august",
            "i_september",
            "j_october",
            "k_november",
            "l_december",
        ].map((key) => locale.get(`zz_month_${key}`));
        SimpleDateFormat.months_short = [
            "a_january",
            "b_february",
            "c_march",
            "d_april",
            "e_may",
            "f_june",
            "g_july",
            "h_august",
            "i_september",
            "j_october",
            "k_november",
            "l_december",
        ].map((key) => locale.get(`zz_month_${key}_short`));
        SimpleDateFormat.days = [
            "a_sunday",
            "b_monday",
            "c_tuesday",
            "d_wednesday",
            "e_thursday",
            "f_friday",
            "g_saturday",
        ].map((key) => locale.get(`zz_weekday_${key}`));
        SimpleDateFormat.firstDayOfWeek = locale.firstDayOfWeek;
        SimpleDateFormat.daysInFirstWeek = locale.daysInFirstWeek;
    }
}
const addZero = function (number) {
    if (number < 10) {
        return "0".concat(number.toString());
    }
    return number;
};
const isMidnight = function (date) {
    if (date instanceof MillenniumDate) {
        return false;
    }
    return date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
};
const getTokens = (input) => {
    const patterns = "yMdEYwaHkKhemsjNbLJve";
    let escaped = false;
    const output = [];
    let lastCharacter = "";
    let currentToken = "";
    let i = 0;
    while (i < input.length) {
        const character = input.substring(i, i + 1);
        if (character === "'") {
            escaped = !escaped;
        }
        else if (!escaped && patterns.indexOf(character) !== -1) {
            if (currentToken && lastCharacter && lastCharacter !== character) {
                output.push({ type: "token", value: currentToken });
                currentToken = "";
            }
            currentToken = currentToken.concat(character);
        }
        else {
            if (currentToken) {
                output.push({ type: "token", value: currentToken });
                currentToken = "";
            }
            output.push({ type: "literal", value: character });
        }
        lastCharacter = character;
        i++;
    }
    if (currentToken) {
        output.push({ type: "token", value: currentToken });
    }
    return output;
};
const endDateTokens = [
    "jjjj",
    "jj",
    "NNNN",
    "NNN",
    "NN",
    "N",
    "bb",
    "b",
    "LLLL",
    "LLL",
    "LL",
    "L",
    "JJJJ",
    "JJ",
    "J",
    "vv",
    "v",
    "ee",
    "e",
];
const containsEndDate = function (format) {
    const tokens = getTokens(format)
        .filter((item) => item.type === "token")
        .map((item) => item.value);
    return tokens.some((token) => endDateTokens.indexOf(token) !== -1);
};
